import {
  IAccrualPairNetworkSignals,
  IRelationEntrySignals,
  IRelationLinkKindMeta,
  IRelationNetworkSignals,
  IRelationType
} from '@query/documentRelations'
import { IBlueprint, IDocument } from '@query/documents'

import { getDocumentAmountForRelation } from './relationAmounts'

export type { IRelationLinkKindMeta }
export type RelationLinkKind = IRelationLinkKindMeta['kind']

export type DocumentRelationContext = {
  sourceDocumentAmount: number
  sourceDocumentBalance: number
  dueAccountEffect: number | null | undefined
  dueAccountTypes: string[] | null | undefined
  hasAssPayEntries: boolean | null | undefined
  availableLinkKinds?: IRelationLinkKindMeta[] | null
  accrualPairDueEffect: number | null | undefined
  hasAccrualPairDueMismatch: boolean | undefined
  accrualPairInvoiceTotal: number | null | undefined
  accrualPairPaymentTotal: number | null | undefined
}

export type DocumentRelationContextInput = {
  [K in keyof DocumentRelationContext]?: DocumentRelationContext[K] | null
}

type DocumentRelationContextSource = Pick<
  IDocument,
  'balance' | 'relation_network_signals' | 'relation_entry_signals' | 'available_link_kinds'
> &
  Partial<Pick<IDocument, 'relations'>>

export const getAccrualPairNetworkSignals = (
  document?: Pick<IDocument, 'relation_network_signals'> | null
): IAccrualPairNetworkSignals | undefined =>
  document?.relation_network_signals?.[IRelationType.ACCRUAL_PAIR]

export const getSalaryPaymentNetworkSignals = (
  document?: Pick<IDocument, 'relation_network_signals'> | null
): IAccrualPairNetworkSignals | undefined =>
  document?.relation_network_signals?.[IRelationType.SALARY_PAYMENT]

export const documentHasRelationDueMismatch = (
  document?: Pick<IDocument, 'relation_network_signals'> | null
): boolean =>
  Boolean(
    getAccrualPairNetworkSignals(document)?.has_due_mismatch ||
      getSalaryPaymentNetworkSignals(document)?.has_due_mismatch
  )

export const getActiveRelationNetworkSignals = (
  document?:
    | (Pick<IDocument, 'relation_network_signals' | 'available_link_kinds'> &
        Partial<Pick<IDocument, 'relations'>>)
    | null
): IAccrualPairNetworkSignals | undefined => {
  const relationType = document?.relations?.[0]?.type
  switch (relationType) {
    case IRelationType.SALARY_PAYMENT:
      return getSalaryPaymentNetworkSignals(document)
    case IRelationType.ACCRUAL_PAIR:
      return getAccrualPairNetworkSignals(document)
    case undefined:
      break
    default: {
      const exhaustive: never = relationType
      return exhaustive
    }
  }
  const prefersSalary = (document?.available_link_kinds ?? []).some(
    item => item.kind === 'salary' || item.kind === 'salaryPayment'
  )
  if (prefersSalary) {
    return getSalaryPaymentNetworkSignals(document)
  }
  return getAccrualPairNetworkSignals(document) ?? getSalaryPaymentNetworkSignals(document)
}

export const getRelationEntrySignals = (
  document?: Pick<IDocument, 'relation_entry_signals'> | null
): IRelationEntrySignals | undefined => document?.relation_entry_signals

/**
 * Aligns with backend `get_related_document_display_amount` /
 * `getDocumentAmountForRelation`: non-zero due effect wins; zero due effect
 * falls back to balance (settlements often have due effect 0).
 */
export const getDocumentRelationContext = (
  document: DocumentRelationContextSource
): DocumentRelationContext => {
  const network = getActiveRelationNetworkSignals(document)
  const entry = getRelationEntrySignals(document)

  return {
    sourceDocumentAmount: getDocumentAmountForRelation(document) ?? 0,
    sourceDocumentBalance: document.balance,
    dueAccountEffect: entry?.due_account_effect,
    dueAccountTypes: entry?.due_account_types,
    hasAssPayEntries: entry?.has_ass_pay_entries,
    availableLinkKinds: document.available_link_kinds ?? null,
    accrualPairDueEffect: network?.due_effect,
    hasAccrualPairDueMismatch: network?.has_due_mismatch,
    accrualPairInvoiceTotal: network?.invoice_total,
    accrualPairPaymentTotal: network?.payment_total
  }
}

export const mergeAccrualPairNetworkIntoDocument = (
  document: IDocument,
  network: IAccrualPairNetworkSignals
): IDocument => {
  const relation_network_signals: IRelationNetworkSignals = {
    ...(document.relation_network_signals ?? {}),
    [IRelationType.ACCRUAL_PAIR]: network
  }

  return {
    ...document,
    relation_network_signals
  }
}

export const getBlueprintAccountIds = (blueprint?: IBlueprint | null): number[] => {
  if (!blueprint) return []

  const accountIds = new Set<number>()

  if (blueprint.debet_account_id != null) {
    accountIds.add(blueprint.debet_account_id)
  }

  if (blueprint.credit_account_id != null) {
    accountIds.add(blueprint.credit_account_id)
  }

  for (const entry of blueprint.debet_entries || []) {
    if (entry.account_id != null) {
      accountIds.add(entry.account_id)
    }
  }

  for (const entry of blueprint.credit_entries || []) {
    if (entry.account_id != null) {
      accountIds.add(entry.account_id)
    }
  }

  for (const entry of blueprint.expense_entries || []) {
    if (entry.account_id != null) {
      accountIds.add(entry.account_id)
    }
  }

  return Array.from(accountIds)
}
